import React, { useEffect, useState } from 'react'
import { useKato } from '../../../contexts/kato.context'
import KatoSelects from '../components/kato-selects.component'
import classes from './styles.module.scss'
import { useTranslation } from 'react-i18next'
import { useShared } from '../../../contexts/shared.context'
import { isEmpty, t } from '../../../helpers/utils'
import Field from '../../../components/field/field.component'
import { GeneralInfoStatus, IBasicStats, IProductSelfSufficiencyStats } from '../../../types/kato.types'
import FooterComponent from '../components/footer.component'
import { ChevronRightIcon } from '../../../assets/icons'
import { getPercentage } from '../../indicators/components/fullscreen/food-fullscreen.component'
import { updateGeneralInfoData } from '../../../requests/supervisor.request'
import { toast } from 'react-toastify'

const baseClass = 'self-sufficiency-tab'
const translationKey = 'admin.self-sufficiency'

const SelfSufficiencyTab = () => {
  const { i18n: { language } } = useTranslation();
  const { katoGeneralInfo, updatedGeneralInfo } = useKato();
  const { basicStats, productSelfSufficiencyStats, status } = katoGeneralInfo || {};
  const { selectedInfoYear } = useShared();
  const [formData, setFormData] = useState({
    basicStats: basicStats || [],
    productSelfSufficiencyStats: productSelfSufficiencyStats || [],
    status: status || GeneralInfoStatus.IN_PROCESS,
  });

  const [openAll, setOpenAll] = useState(false);
  const [openedProducts, setOpenedProducts] = useState<Record<number, boolean>>({});
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const toggleProduct = (id: number) => {
    setOpenedProducts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = () => {
    const allOpen = !openAll;
    const updated = Object.fromEntries(
      Object.values(productSelfSufficiencyStats).map(stat => [stat.id, allOpen])
    );
    setOpenedProducts(updated);
    setOpenAll(allOpen);
  };

  const handleSave = () => {
    if (validateFields()) {
      const data = {
        basicStats: formData.basicStats,
        productSelfSufficiencyStats: formData.productSelfSufficiencyStats,
        status: formData.status,
      }
      updateGeneralInfoData(
        katoGeneralInfo.kato,
        data,
        selectedInfoYear
      ).then(() => {
        updatedGeneralInfo();
        toast.success(t(`admin.success`, language));
      });
    }
  }

  const validateFields = () => {
    const newErrors: Record<string, boolean> = {};
    const fields = formData.basicStats.filter(stat => stat.type === 'FOOD');

    const hasAtLeastOneValue = fields.some(stat =>
      stat.previousYearVal != null || stat.currentYearVal != null
    );

    if (hasAtLeastOneValue) {
      for (const stat of fields) {
        if (isEmpty(stat.previousYearVal) || isEmpty(stat.currentYearVal)) {
          newErrors[`basicStats.${stat.subType}.currentYearVal`] = isEmpty(stat.currentYearVal);
          newErrors[`basicStats.${stat.subType}.previousYearVal`] = isEmpty(stat.previousYearVal);
        }
      }
    }

    setErrors(newErrors);
    return Object.values(newErrors).every(error => !error);
  };

  useEffect(() => {
    setFormData({
      basicStats: basicStats || [],
      productSelfSufficiencyStats: productSelfSufficiencyStats || [],
      status: status || GeneralInfoStatus.IN_PROCESS,
    });
    // setOpenedProducts({});
    // setOpenAll(false);
  }, [basicStats, katoGeneralInfo.kato, selectedInfoYear, productSelfSufficiencyStats]);

  const renderFields = (lang: 'ru' | 'kz') => {
    const suffix = lang === 'ru' ? 'Ru' : 'Kz';
    return <div className={classes['fields-container']}>
      {
        formData.basicStats.filter(stat => stat.type === 'FOOD').map((stat) => (
          <div key={stat.subType} className={classes['field-block']}>
            <div className={classes['field-block__title']}>{t(`${translationKey}.${stat.subType}.title`, lang)}</div>
            <div className={classes['field-block__fields']}>
              {
                ['previousYearVal', 'currentYearVal'].map((key) => {
                  const fieldKey = key as keyof Omit<IBasicStats, 'additionalInfo'>;
                  const value = stat?.[fieldKey] ?? '';
                  return (
                    <Field
                      key={`${stat.subType}-${key}-${lang}`}
                      value={value ?? ''}
                      label={t(`${translationKey}.yearVal`, lang, { year: key === 'previousYearVal' ? selectedInfoYear - 1 : selectedInfoYear, subtype: t(`${translationKey}.${stat.subType}.yearVal`, lang) })}
                      type="number"
                      onChange={(newVal) => {
                        const val = newVal === '' ? null : Math.min(+newVal, 100);
                        const updatedData = formData.basicStats.map(item => item.subType === stat.subType ? {
                          ...item,
                          [fieldKey]: val
                        } : item)
                        setFormData(prev => ({
                          ...prev,
                          basicStats: updatedData
                        }));
                      }}
                      required
                      error={errors[`basicStats.${stat.subType}.${fieldKey}`]}
                    />
                  )
                })
              }
            </div>
          </div>
        ))
      }

      <div className={`${classes['field-block']} ${classes[`${baseClass}__stats`]}`}>
        <div onClick={toggleAll} className={classes['field-block__title']}>
          {t(`${translationKey}.stats.title`, lang)} <ChevronRightIcon style={{ transform: openAll ? 'rotate(90deg)' : 'rotate(0deg)' }} />
        </div>
        {
          formData.productSelfSufficiencyStats?.map((item) => {
            const isOpen = openedProducts[item.id] || false;
            return (
              <div className={classes['field-block']} key={item.id} >
                <div className={classes['field-block__title']} onClick={() => toggleProduct(item.id)}>
                  {item[`productName${suffix}`]} <ChevronRightIcon style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }} />
                </div>
                {
                  isOpen && <div className={classes['field-block__fields']}>
                    {[
                      'currentYearVal',
                      'previousYearVal',
                      'annualConsumptionPerPerson',
                      'annualConsumption',
                      'annualProductProduction',
                      'selfSufficiencyPercentage'
                    ].map((key) => {
                      const value = key === 'selfSufficiencyPercentage' ? getPercentage(item) : item[key as keyof Omit<IProductSelfSufficiencyStats, 'additionalInfo'>] ?? '';
                      return (
                        <Field
                          key={`${item.id}-${key}-${lang}`}
                          value={value ?? ''}
                          label={t(`${translationKey}.${key}`, lang, { year: key === 'currentYearVal' ? selectedInfoYear : selectedInfoYear - 1 })}
                          type="number"
                          onChange={(val) => {
                            const newVal = val === '' ? null : +val;
                            const updatedStats = formData.productSelfSufficiencyStats.map(stat => stat.id === item.id ? {
                              ...stat,
                              [key as keyof Omit<IProductSelfSufficiencyStats, 'additionalInfo'>]: newVal
                            } : stat);
                            setFormData(prev => ({
                              ...prev,
                              productSelfSufficiencyStats: updatedStats
                            }));
                          }}
                          required
                          disabled={'selfSufficiencyPercentage' === key}
                        />
                      );
                    })}
                  </div>
                }
              </div>
            )
          })
        }
      </div>
    </div >
  }

  if (!basicStats || !productSelfSufficiencyStats) return null;

  return (
    <div className={classes[baseClass]}>
      <div className={classes[`grid`]}>
        <KatoSelects lang={language as 'ru' | 'kz'} />
        <KatoSelects lang={language === 'ru' ? 'kz' : 'ru'} />
        {renderFields(language as 'ru' | 'kz')}
        {renderFields(language === 'ru' ? 'kz' : 'ru')}
      </div>

      <FooterComponent
        onSave={handleSave}
        status={formData.status}
        validateFields={validateFields}
      />
    </div>
  )
}

export default SelfSufficiencyTab